import React, { useState, useRef, useEffect } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./SingleCalendarDateComparison.css";

export default function SingleCalendarDateComparison({ 
  oldStartDate, 
  oldEndDate, 
  newStartDate, 
  newEndDate,
  onDatesChange,
  darkMode,
  disabled = false
}) {
  const [error, setError] = useState(null);
  const [selectionStep, setSelectionStep] = useState(0);
  const [tempDate, setTempDate] = useState(null);
  
  // Add ref for the scrollable container
  const calendarContainerRef = useRef(null);
  
  // Scroll to bottom on initial render
  useEffect(() => {
    if (calendarContainerRef.current) {
      // Scroll to the bottom of the container
      calendarContainerRef.current.scrollTop = calendarContainerRef.current.scrollHeight;
    }
  }, []); // Empty dependency array ensures this runs only once on mount
  
  // Calculate how many months to show
  const monthsToShow = 16; // Show up to 16 months for Search Console data
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return "Select date...";
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Reset all states including the parent's dates
  const resetSelection = () => {
    if (disabled) return;
    
    setError(null);
    setSelectionStep(0);
    setTempDate(null);
    // Reset all dates in parent component
    onDatesChange({
      oldStartDate: null,
      oldEndDate: null,
      newStartDate: null,
      newEndDate: null
    });
  };

  // Helper function to format date for backend
  const formatDateForBackend = (date) => {
    if (!date) return null;
    const d = new Date(date);
    // Use local date formatting methods instead of ISO to prevent timezone issues
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0'); // getMonth() is 0-indexed
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to calculate days between dates
  const getDaysBetween = (start, end) => {
    if (!start || !end) return null;
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days
  };

  // Handle date selection based on the current step
  const handleDateSelect = (date) => {
    if (!date || disabled) return;
    
    setTempDate(null);
    
    switch(selectionStep) {
      case 0: // First click - new period first date
        setTempDate(date);
        setSelectionStep(1);
        break;
      case 1: // Second click - new period second date
        if (date.getTime() === tempDate.getTime()) {
          // If clicking the same date, use it as both start and end
          handleDateChange({
            newStartDate: date,
            newEndDate: date
          });
        } else if (date < tempDate) {
          // If second date is before first, use it as start
          handleDateChange({
            newStartDate: date,
            newEndDate: tempDate
          });
        } else {
          // Otherwise use it as end
          handleDateChange({
            newStartDate: tempDate,
            newEndDate: date
          });
        }
        setTempDate(null);
        setSelectionStep(2);
        break;
      case 2: // Third click - old period first date
        // Validate that old period date is before new period
        if (newStartDate && date >= newStartDate) {
          setError("Old period must be before new period");
          return;
        }
        setTempDate(date);
        setError(null);
        setSelectionStep(3);
        break;
      case 3: // Fourth click - old period second date
        // Validate that old period end date is before new period
        if (newStartDate && date >= newStartDate) {
          setError("Old period must be before new period");
          return;
        }
        
        if (date.getTime() === tempDate.getTime()) {
          // If clicking the same date, use it as both start and end
          handleDateChange({
            oldStartDate: date,
            oldEndDate: date
          });
        } else if (date < tempDate) {
          // If second date is before first, use it as start
          handleDateChange({
            oldStartDate: date,
            oldEndDate: tempDate
          });
        } else {
          // Otherwise use it as end
          handleDateChange({
            oldStartDate: tempDate,
            oldEndDate: date
          });
        }
        setTempDate(null);
        setError(null);
        setSelectionStep(4);
        break;
      case 4: // Selection complete - start over if clicked again
        resetSelection();
        setTempDate(date);
        setSelectionStep(1);
        break;
      default:
        break;
    }
  };

  // Handle quick select options
  const handleQuickSelect = (range) => {
    if (disabled) return;
    
    // Prevent any ongoing selection
    setSelectionStep(0);
    setTempDate(null);
    setError(null);
    
    // Calculate the date ranges based on the quick select option
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let newStart, newEnd, oldStart, oldEnd;
    
    switch(range.value) {
      case 'last7':
        // Last 7 days vs previous 7 days
        newEnd = new Date(today);
        newStart = new Date(today);
        newStart.setDate(today.getDate() - 6); // Last 7 days including today
        
        // Old period should end the day before new period starts
        oldEnd = new Date(newStart);
        oldEnd.setDate(oldEnd.getDate() - 1);
        
        oldStart = new Date(oldEnd);
        oldStart.setDate(oldStart.getDate() - 6); // Previous 7 days
        
        break;
        
      case 'last30':
        // Last 30 days vs previous 30 days
        newEnd = new Date(today);
        newStart = new Date(today);
        newStart.setDate(today.getDate() - 29); // Last 30 days including today
        
        // Old period should end the day before new period starts
        oldEnd = new Date(newStart);
        oldEnd.setDate(oldEnd.getDate() - 1);
        
        oldStart = new Date(oldEnd);
        oldStart.setDate(oldStart.getDate() - 29); // Previous 30 days
        
        break;
        
      case 'thisMonth':
        // This month vs last month
        newEnd = new Date(today);
        newStart = new Date(today.getFullYear(), today.getMonth(), 1);
        
        oldEnd = new Date(newStart);
        oldEnd.setDate(oldEnd.getDate() - 1);
        oldStart = new Date(oldEnd.getFullYear(), oldEnd.getMonth(), 1);
        break;
        
      case 'lastMonth':
        // Last month vs previous month
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        newStart = new Date(lastMonth);
        newEnd = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of last month
        
        oldStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        oldEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);
        break;
        
      case 'thisYear':
        // This year vs last year (same period)
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const currentDay = today.getDate();
        
        newStart = new Date(currentYear, 0, 1); // Jan 1 of current year
        newEnd = new Date(today);
        
        oldStart = new Date(currentYear - 1, 0, 1); // Jan 1 of previous year
        oldEnd = new Date(currentYear - 1, currentMonth, currentDay);
        break;
        
      default:
        return; // Invalid option
    }
    
    // Update the dates
    handleDateChange({
      newStartDate: newStart,
      newEndDate: newEnd,
      oldStartDate: oldStart,
      oldEndDate: oldEnd
    });
  };

  // Validate and format dates before sending to backend
  const handleDateChange = (updates) => {
    // First update the UI with the raw Date objects
    onDatesChange({
      ...updates,
      formattedDates: null // Reset formatted dates until we have complete ranges
    });
  
    // If we have complete ranges, prepare data for backend
    if (
      (updates.newStartDate && updates.newEndDate) || 
      (updates.oldStartDate && updates.oldEndDate)
    ) {
      // Get current state including updates
      const currentState = {
        newStartDate: updates.newStartDate || newStartDate,
        newEndDate: updates.newEndDate || newEndDate,
        oldStartDate: updates.oldStartDate || oldStartDate,
        oldEndDate: updates.oldEndDate || oldEndDate
      };
  
      // Only proceed if we have complete ranges
      if (
        currentState.newStartDate && 
        currentState.newEndDate && 
        currentState.oldStartDate && 
        currentState.oldEndDate
      ) {
        // Validate ranges
        if (
          currentState.oldEndDate < currentState.oldStartDate ||
          currentState.newEndDate < currentState.newStartDate ||
          currentState.newStartDate <= currentState.oldEndDate
        ) {
          setError("Invalid date ranges");
          return;
        }
  
        // Correctly format dates for App.jsx
        const correctlyFormattedDates = {
          new_period: `${formatDateForBackend(currentState.newStartDate)} - ${formatDateForBackend(currentState.newEndDate)}`,
          old_period: `${formatDateForBackend(currentState.oldStartDate)} - ${formatDateForBackend(currentState.oldEndDate)}`,
        };
  
        // Pass all four Date objects and the correctly formatted strings back to parent
        onDatesChange({
          newStartDate: currentState.newStartDate,
          newEndDate: currentState.newEndDate,
          oldStartDate: currentState.oldStartDate,
          oldEndDate: currentState.oldEndDate,
          formattedDates: correctlyFormattedDates 
        });
      }
    }
  };

  // Custom day rendering to highlight selected ranges
  const renderDayContents = (day, date) => {
    // Create a string version for comparison
    const dateStr = date.toISOString().split('T')[0];
    const classes = [];
    
    // Check if date is in new range
    if (newStartDate && newEndDate && date >= newStartDate && date <= newEndDate) {
      classes.push('new-range-day');
    }
    
    // Check if date is in old range
    if (oldStartDate && oldEndDate && date >= oldStartDate && date <= oldEndDate) {
      classes.push('old-range-day');
    }
    
    // Check if date is the temp selection
    if (tempDate && dateStr === tempDate.toISOString().split('T')[0]) {
      if (selectionStep === 1) {
        classes.push('new-range-temp');
      } else if (selectionStep === 3) {
        classes.push('old-range-temp');
      }
    }
    
    return (
      <div className={`day-content ${classes.join(' ')}`}>
        {day}
      </div>
    );
  };

  // Quick select options
  const quickSelectRanges = [
    {
      label: "Last 7 days vs previous",
      value: 'last7',
      getValue: () => {
        const today = new Date();
        const newEnd = new Date(today);
        const newStart = new Date(today);
        newStart.setDate(today.getDate() - 6); // Last 7 days including today
        
        // Old period should end the day before new period starts
        const oldEnd = new Date(newStart);
        oldEnd.setDate(oldEnd.getDate() - 1);
        
        const oldStart = new Date(oldEnd);
        oldStart.setDate(oldStart.getDate() - 6); // Previous 7 days
        
        return { newStart, newEnd, oldStart, oldEnd };
      }
    },
    {
      label: "Last 30 days vs previous",
      value: 'last30',
      getValue: () => {
        const today = new Date();
        const newEnd = new Date(today);
        const newStart = new Date(today);
        newStart.setDate(today.getDate() - 29); // Last 30 days including today
        
        // Old period should end the day before new period starts
        const oldEnd = new Date(newStart);
        oldEnd.setDate(oldEnd.getDate() - 1);
        
        const oldStart = new Date(oldEnd);
        oldStart.setDate(oldStart.getDate() - 29); // Previous 30 days
        
        return { newStart, newEnd, oldStart, oldEnd };
      }
    },
    {
      label: "This month vs last month",
      value: 'thisMonth',
      getValue: () => {
        const today = new Date();
        
        // New period (this month)
        const newEnd = new Date(today);
        const newStart = new Date(today.getFullYear(), today.getMonth(), 1);
        
        // Old period (last month)
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        
        return { 
          newStart, 
          newEnd, 
          oldStart: lastMonthStart, 
          oldEnd: lastMonthEnd 
        };
      }
    },
    {
      label: "Last month vs previous",
      value: 'lastMonth',
      getValue: () => {
        const today = new Date();
        
        // Last month
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        
        // Month before last
        const twoMonthsAgoEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);
        const twoMonthsAgoStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        
        return { 
          newStart: lastMonthStart, 
          newEnd: lastMonthEnd, 
          oldStart: twoMonthsAgoStart, 
          oldEnd: twoMonthsAgoEnd 
        };
      }
    },
    {
      label: "This year vs last year",
      value: 'thisYear',
      getValue: () => {
        const today = new Date();
        
        // This year to date
        const newEnd = new Date(today);
        const newStart = new Date(today.getFullYear(), 0, 1);
        
        // Same period last year
        const oldEnd = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
        const oldStart = new Date(today.getFullYear() - 1, 0, 1);
        
        return { newStart, newEnd, oldStart, oldEnd };
      }
    }
  ];

  // Get the helper text based on the current selection step
  const getSelectionHelperText = () => {
    if (disabled) return "Date selection disabled";
    
    switch(selectionStep) {
      case 0:
        return "Select new period start date";
      case 1:
        return "Select new period end date";
      case 2:
        return "Select old period start date";
      case 3:
        return "Select old period end date";
      case 4:
        return "Date selection complete";
      default:
        return "";
    }
  };

  // Generate an array of months to display
  const getMonthsToShow = () => {
    const months = [];
    const today = new Date();
    
    // Start with current month and go back 15 months (total 16 months)
    for (let i = 0; i < monthsToShow; i++) {
      const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push(monthDate);
    }
    
    return months.reverse(); // Show oldest to newest
  };

  return (
    <div className="space-y-3">
      {/* Quick Select Dropdown - Optimized for sidebar */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${
          darkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          Quick Select
        </label>
        <select
          disabled={disabled}
          onChange={(e) => {
            if (disabled || e.target.value === '') return;
            
            const selectedRange = quickSelectRanges.find(range => range.value === e.target.value);
            if (selectedRange) {
              const dates = selectedRange.getValue();
              handleDateChange({
                newStartDate: dates.newStart,
                newEndDate: dates.newEnd,
                oldStartDate: dates.oldStart,
                oldEndDate: dates.oldEnd
              });
              setSelectionStep(4);
              
              // Scroll to current month
              if (calendarContainerRef.current) {
                setTimeout(() => {
                  calendarContainerRef.current.scrollTop = calendarContainerRef.current.scrollHeight;
                }, 0);
              }
            }
            // Reset select to default
            e.target.value = '';
          }}
          className={`w-full rounded-md border shadow-sm text-sm py-2 px-3
            ${darkMode
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-900'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <option value="">Choose a date range...</option>
          {quickSelectRanges.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </select>
      </div>

      {/* Calendar - Full width for sidebar */}
      <div className={`p-3 rounded-lg border ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } ${disabled ? 'opacity-50' : ''}`}>
        <div className="flex justify-between items-center mb-3">
          <h4 className={`text-sm font-medium ${
            darkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            {getSelectionHelperText()}
          </h4>
          <div className="flex space-x-2">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>New</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-orange-500 rounded-full mr-1"></div>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Old</span>
            </div>
          </div>
        </div>
        
        {error && (
          <p className="mb-2 text-sm text-red-500 bg-red-100 dark:bg-red-900/30 p-2 rounded">
            ⚠️ {error}
          </p>
        )}
        
        {/* Fixed day headers above the scrollable container */}
        <div className="grid grid-cols-7 text-center mb-2" style={{maxWidth: '280px'}}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, index) => (
            <div 
              key={index} 
              className={`text-xs font-medium py-1 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              {day}
            </div>
          ))}
        </div>
        
        {/* Multi-month scrollable calendar view - Optimized for sidebar */}
        <div 
          ref={calendarContainerRef}
          className="calendar-container"
          style={{ 
            maxHeight: '280px',
            maxWidth: '280px',
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: 'white',
            borderRadius: '0.375rem',
            width: '100%',
            padding: '4px',
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e0 #f7fafc',
            pointerEvents: disabled ? 'none' : 'auto'
          }}
        >
          {getMonthsToShow().map((monthDate, index) => (
            <div key={index} style={{ margin: 0, padding: 0 }}>
              <DatePicker
                inline
                selected={null}
                onChange={handleDateSelect}
                maxDate={new Date()}
                renderDayContents={renderDayContents}
                calendarClassName="date-comparison-calendar"
                showMonthYearPicker={false}
                showFullMonthYearPicker={false}
                showDisabledMonthNavigation={true}
                showPreviousMonths={false}
                showNextMonths={false}
                showOtherMonths={false}
                openToDate={monthDate}
                disabledKeyboardNavigation
                showMonthDropdown={false}
                showYearDropdown={false}
                showPopperArrow={false}
                showNavigation={false}
                formatWeekDay={() => ''}
                monthsShown={1}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
        
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={disabled}
            onClick={resetSelection}
            className={`px-2 py-1 text-xs rounded-md ${
              disabled
                ? 'cursor-not-allowed opacity-50 bg-gray-300 text-gray-500'
                : darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
} 